// src/components/WebinarSignupForm.jsx
import React, { useState, useMemo } from "react";
import styled from "styled-components";

/* ================= API base ================= */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5000";

/* ================= Styled ================= */
const Shell = styled.section``;

const Card = styled.div`
  /* Theme tokens via CSS variables for light/dark variants */
  --fg: ${(p) => (p.$variant === "dark" ? "#ffffff" : "#0f172a")};
  --muted: ${(p) => (p.$variant === "dark" ? "#cfe7ff" : "#64748b")};
  --label: ${(p) => (p.$variant === "dark" ? "#e6effa" : "#0f172a")};
  --border: ${(p) => (p.$variant === "dark" ? "rgba(255,255,255,0.18)" : "#e5edf7")};
  --input-border: ${(p) => (p.$variant === "dark" ? "rgba(255,255,255,0.2)" : "#dbe5ef")};
  --input-bg: ${(p) => (p.$variant === "dark" ? "rgba(8, 15, 26, 0.55)" : "#f8fafc")};
  --input-ph: ${(p) => (p.$variant === "dark" ? "rgba(255,255,255,.6)" : "#94a3b8")};
  --focus: #7fd1ff;  /* focus ring */
  --cta-grad: linear-gradient(135deg, #ff914d, #ff6b35);
  --card: ${(p) => (p.$variant === "dark" ? "#0f1b2d" : "#ffffff")};
  --shadow: ${(p) =>
    p.$variant === "dark" ? "0 14px 34px rgba(0,0,0,.28)" : "0 14px 40px rgba(2,6,23,.08)"};

  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 16px;
  overflow: hidden;
  color: var(--fg);
  box-shadow: var(--shadow);
  ${(p) => (p.$variant === "dark" ? "backdrop-filter:saturate(135%) blur(8px);" : "")}
`;

const Header = styled.div`
  background: linear-gradient(135deg, #3dccc7 0%, #2a5a7a 100%);
  color: #fff;
  padding: clamp(14px, 3.4vw, 18px) clamp(16px, 3.8vw, 22px);
  display: grid;
  gap: 8px;
`;

const Title = styled.h2`
  margin: 0;
  font-weight: 900;
  font-size: clamp(1.1rem, 3.6vw, 1.5rem);
  letter-spacing: 0.2px;
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.22);
  color: #eef8ff;
  font-size: 0.88rem;
  font-weight: 700;

  svg { opacity: .9 }
`;

const Body = styled.div`
  padding: clamp(16px, 4vw, 22px);
  display: grid;
  gap: 16px;
`;

/* Two-column layout INSIDE the card */
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: clamp(12px, 3.6vw, 22px);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* Left — details */
const Details = styled.aside`
  border: 1px solid #e8eff7;
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
`;

const DetailsHead = styled.div`
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  color: #fff;
  background: linear-gradient(135deg, #3dccc7 0%, #2a5a7a 100%);
`;

const IconBox = styled.div`
  width: 56px; height: 56px;
  border-radius: 12px;
  background: rgba(255,255,255,0.16);
  border: 1px solid rgba(255,255,255,0.26);
  display: grid; place-items: center;
`;

const DetailsHeadText = styled.div` display: grid; gap: 6px; `;

const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 4px 10px; border-radius: 999px;
  background: rgba(255,255,255,0.16);
  border: 1px solid rgba(255,255,255,0.28);
  font-weight: 800; font-size: 0.8rem;
`;

const DetailsTitle = styled.h3`
  margin: 0; font-size: clamp(1.05rem, 3.2vw, 1.35rem); font-weight: 900;
`;

const DetailsBody = styled.div`
  padding: 12px 14px;
  background: #f8fbff;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dashed #e6eef7;
  &:last-child { border-bottom: 0; }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const K = styled.div` color:#5b6b7f; font-weight:700; font-size:.95rem; `;
const V = styled.div` color:#0b1320; font-weight:800; font-size:1.02rem; `;
const Desc = styled.div` margin-top: 10px; color:#213449; line-height:1.55; `;

/* Right — form */
const FormCol = styled.div``;

const Intro = styled.p`
  margin: 0 0 10px;
  font-size: 0.96rem;
  color: var(--muted);
`;

const Divider = styled.hr`
  border: 0;
  height: 1px;
  background: ${(p) => (p.$variant === "dark" ? "rgba(255,255,255,.12)" : "#eef2f6")};
  margin: 10px 0 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: var(--label);
  font-weight: 700;
`;

const InputWrap = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 36px 1fr;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  overflow: hidden;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:focus-within {
    outline: 2px solid var(--focus);
    border-color: var(--focus);
    box-shadow: 0 0 0 3px rgba(127,209,255,0.18) inset;
  }

  svg { margin-left: 10px; opacity: 0.7; }
`;

const Input = styled.input`
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--fg);
  padding: 12px 12px 12px 0;
  &::placeholder { color: var(--input-ph); }
  &:focus { outline: none; }
`;

const Actions = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap; margin-top: 6px;
`;

const Submit = styled.button`
  background: var(--cta-grad);
  color: #fff;
  border: 0;
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 12px 26px rgba(255,107,53,.35);
  transition: transform .12s ease, box-shadow .12s ease, opacity .12s ease;
  &:hover { transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Note = styled.p`
  margin: 6px 0 0;
  font-size: 0.92rem;
  line-height: 1.35;
  color: ${(p) => (p.error ? "#b91c1c" : "var(--muted)")};
`;

const Success = styled.div`
  background: rgba(34,197,94,.12);
  border: 1px solid rgba(34,197,94,.35);
  color: #14532d;
  padding: 12px 14px;
  border-radius: 12px;
  font-weight: 700;
  margin-top: 8px;
`;

const EmptyState = styled.div`
  padding: 10px 0 0;
  color: var(--muted);
`;

/* ================= Helpers ================= */
function formatWhen(webinar) {
  if (!webinar?.startsAt) return null;
  const tz = webinar.timezone || "Asia/Kolkata";
  try {
    const dt = new Date(webinar.startsAt);
    const text = dt.toLocaleString("en-IN", {
      timeZone: tz,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${text} (${tz})`;
  } catch {
    return null;
  }
}

/* ================= Component ================= */
export default function WebinarSignupForm({
  webinar,
  onSuccess,
  title = "Save your seat",
  variant = "light", // "light" | "dark"
}) {
  const [$form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [isError, setIsError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Honeypot (simple bot trap)
  const [website, setWebsite] = useState("");

  const canSubmit = useMemo(
    () =>
      webinar &&
      webinar._id &&
      $form.firstName.trim().length > 0 &&
      $form.email.trim().length > 0 &&
      !loading,
    [webinar, $form.firstName, $form.email, loading]
  );

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  async function register(webinarId, payload) {
    const r = await fetch(`${API_BASE}/api/webinars/${webinarId}/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return r.json();
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setShowSuccess(false);
    setIsError(false);

    // If honeypot filled, silently ignore
    if (website) {
      setIsError(true);
      setMsg("Validation failed.");
      return;
    }

    if (!canSubmit) {
      setIsError(true);
      setMsg("First name and Email are required.");
      return;
    }
    setLoading(true);
    try {
      const res = await register(webinar._id, { ...$form, consent: true });
      if (res?.ok) {
        setShowSuccess(true);
        setIsError(false);
        setMsg("You're registered! Check your email for the calendar invite and joining link.");
        onSuccess?.(res);
        setForm({ firstName: "", lastName: "", email: "" });
      } else {
        setIsError(true);
        setMsg(res?.error || "Something went wrong.");
      }
    } catch {
      setIsError(true);
      setMsg("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const whenText = formatWhen(webinar);

  return (
    <Shell>
      <Card $variant={variant}>
        <Header>
          <Title>{title}</Title>
          <HeaderMeta>
            <Pill title="Webinar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M3 6.5A2.5 2.5 0 0 1 5.5 4h13A2.5 2.5 0 0 1 21 6.5v7A2.5 2.5 0 0 1 18.5 16h-13A2.5 2.5 0 0 1 3 13.5v-7Z" stroke="currentColor" strokeWidth="1.6" />
                <path d="M12 8.2a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z" stroke="currentColor" strokeWidth="1.6" />
                <path d="M7 18.5h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
              {webinar?.title || "Webinar"}
            </Pill>
            {whenText && (
              <Pill title="Date & time">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M7 3v3M17 3v3M3.5 9h17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <rect x="4" y="6.5" width="16" height="13" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                </svg>
                {whenText}
              </Pill>
            )}
          </HeaderMeta>
        </Header>

        <Body>
          {!webinar ? (
            <EmptyState>
              <strong>No upcoming webinar yet.</strong> We’re lining up the next session. Check back soon or join our email list.
            </EmptyState>
          ) : (
            <ContentGrid>
              {/* LEFT: DETAILS */}
              <Details>
                <DetailsHead>
                  <IconBox aria-hidden>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                      <rect x="2.75" y="4.75" width="18.5" height="12.5" rx="2.5"
                        stroke="#fff" strokeWidth="1.5"/>
                      <circle cx="12" cy="11" r="3.25" stroke="#fff" strokeWidth="1.5"/>
                      <path d="M7 19.25h10" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </IconBox>
                  <DetailsHeadText>
                    <Badge>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M7 3v3M17 3v3M3.5 9h17" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                      Next Webinar
                    </Badge>
                    <DetailsTitle>{webinar.title}</DetailsTitle>
                  </DetailsHeadText>
                </DetailsHead>

                <DetailsBody>
                  <Row><K>Topic</K><V>{webinar.topic}</V></Row>
                  <Row><K>Date &amp; Time</K><V>{whenText}</V></Row>
                  <Row><K>Duration</K><V>{(webinar.durationMins || 60) + " mins"}</V></Row>
                  {!!webinar.hostNames?.length && (
                    <Row><K>Hosts</K><V>{webinar.hostNames.join(", ")}</V></Row>
                  )}
                  {!!webinar.description && (
                    <Desc dangerouslySetInnerHTML={{ __html: webinar.description }} />
                  )}
                </DetailsBody>
              </Details>

              {/* RIGHT: FORM */}
              <FormCol>
                <Intro>
                  Join us for a focused session on <strong>{webinar?.topic || "our topic"}</strong>.
                  You’ll receive the joining link and a calendar invite by email.
                </Intro>

                <Divider $variant={variant} />

                <form onSubmit={handleSubmit} noValidate>
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    style={{ display: "none" }}
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <Grid>
                    <Field>
                      <Label htmlFor="firstName">First name*</Label>
                      <InputWrap>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5Z" stroke="currentColor" strokeWidth="1.6"/>
                        </svg>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={$form.firstName}
                          onChange={onChange}
                          placeholder="first name.."
                          autoComplete="given-name"
                        />
                      </InputWrap>
                    </Field>

                    <Field>
                      <Label htmlFor="lastName">Last name</Label>
                      <InputWrap>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5Z" stroke="currentColor" strokeWidth="1.6"/>
                        </svg>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={$form.lastName}
                          onChange={onChange}
                          placeholder="last name.."
                          autoComplete="family-name"
                        />
                      </InputWrap>
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <Label htmlFor="email">Email*</Label>
                      <InputWrap>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path d="M3.5 7.5A2.5 2.5 0 0 1 6 5h12a2.5 2.5 0 0 1 2.5 2.5v9A2.5 2.5 0 0 1 18 19H6a2.5 2.5 0 0 1-2.5-2.5v-9Z" stroke="currentColor" strokeWidth="1.6"/>
                          <path d="m4 7.5 8 5 8-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <Input
                          id="email"
                          type="email"
                          name="email"
                          value={$form.email}
                          onChange={onChange}
                          placeholder="you@gmail.com"
                          autoComplete="email"
                          inputMode="email"
                        />
                      </InputWrap>
                    </Field>
                  </Grid>

                  <Actions>
                    <Submit disabled={!canSubmit}>
                      {loading ? "Submitting..." : "Register"}
                    </Submit>
                  </Actions>

                  <Note>We’ll email the joining link and a calendar invite.</Note>
                  {msg && (
                    <Note error={isError} style={{ marginTop: 6 }}>
                      {msg}
                    </Note>
                  )}
                  {showSuccess && !isError && (
                    <Success aria-live="polite">
                      Registration successful. Check your inbox for details!
                    </Success>
                  )}
                </form>
              </FormCol>
            </ContentGrid>
          )}
        </Body>
      </Card>
    </Shell>
  );
}
